'use strict';

/**
 * Wishlist "compare hint" dialog.
 *
 * Shows a one-off Lottie hint on the wishlist page once the customer has more
 * than one wishlisted item, telling them how to use the compare icon.
 * The animation loops while the dialog is open, which the customer closes with
 * the close button, Esc, or a backdrop click. Dismissed permanently through the
 * "Don't show this again" checkbox.
 */
(function () {
  const DISMISS_KEY = 'glozin__wishlist-compare-hint-dismissed';
  const WISHLIST_ITEMS_KEY = 'glozin__wishlist-items';
  const GRID_WAIT_TIMEOUT = 6000;
  const GRID_POLL_INTERVAL = 150;

  const isDismissed = () => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch (e) {
      return false;
    }
  };

  const setDismissed = (dismissed) => {
    try {
      if (dismissed) {
        localStorage.setItem(DISMISS_KEY, '1');
      } else {
        localStorage.removeItem(DISMISS_KEY);
      }
    } catch (e) {
      console.warn('[wishlist-hint] Unable to persist dismiss state.', e);
    }
  };

  const getWishlistItems = () => {
    const controller = window.WishlistController;

    if (controller && typeof controller.getItems === 'function') {
      try {
        const items = controller.getItems();
        if (Array.isArray(items)) return items;
      } catch (e) {
        console.warn('[wishlist-hint] Unable to read WishlistController.', e);
      }
    }

    try {
      const items = JSON.parse(
        localStorage.getItem(WISHLIST_ITEMS_KEY) || '[]',
      );
      return Array.isArray(items) ? items : [];
    } catch (e) {
      return [];
    }
  };

  const initWishlistCompareHint = () => {
    if (window.wishlistCompareHintInitialized) return;

    const dialog = document.querySelector('[data-wishlist-hint-dialog]');
    if (!dialog) return;

    window.wishlistCompareHintInitialized = true;

    const animationContainer = dialog.querySelector(
      '[data-wishlist-hint-animation]',
    );
    const dismissCheckbox = dialog.querySelector('[data-wishlist-hint-dismiss]');
    const closeButtons = dialog.querySelectorAll('[data-wishlist-hint-close]');
    const lottieSrc = dialog.dataset.lottieSrc;
    const minimumItems = Math.max(1, Number(dialog.dataset.minimumItems || 2));
    const showDelay = Math.max(0, Number(dialog.dataset.showDelay || 3000));
    const canShowModal = typeof dialog.showModal === 'function';
    /**
     * Optional container the wishlist lives in — on the account page the list
     * sits in a tab that is hidden until "My Wishlist" is selected, so the hint
     * has to wait for that tab instead of firing on page load.
     */
    const scopeElement = dialog.dataset.scopeSelector
      ? document.querySelector(dialog.dataset.scopeSelector)
      : null;

    let lottieInstance = null;
    let lottieLoaderPromise = null;
    let lottieReadyPromise = null;
    let lottieIsReady = false;
    let hasShown = false;
    let isPending = false;

    const isScopeVisible = () => {
      if (!scopeElement) return true;

      return !!(
        scopeElement.offsetWidth ||
        scopeElement.offsetHeight ||
        scopeElement.getClientRects().length
      );
    };

    const loadLottie = () => {
      if (window.lottie) return Promise.resolve(window.lottie);
      if (lottieLoaderPromise) return lottieLoaderPromise;

      lottieLoaderPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src =
          'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js';
        script.async = true;
        script.onload = () => {
          if (window.lottie) {
            resolve(window.lottie);
          } else {
            reject(new Error('Lottie failed to initialize.'));
          }
        };
        script.onerror = () => reject(new Error('Failed to load Lottie script.'));
        document.head.appendChild(script);
      });

      return lottieLoaderPromise;
    };

    const resetAnimation = () => {
      if (!lottieInstance) return;
      if (typeof lottieInstance.stop === 'function') {
        lottieInstance.stop();
      }
      if (typeof lottieInstance.goToAndStop === 'function') {
        lottieInstance.goToAndStop(0, true);
      }
    };

    const prepareAnimation = () => {
      if (!lottieSrc || !animationContainer) {
        return Promise.reject(new Error('Missing Lottie animation source.'));
      }
      if (lottieInstance && lottieIsReady) {
        return Promise.resolve(lottieInstance);
      }
      if (lottieReadyPromise) return lottieReadyPromise;

      lottieReadyPromise = loadLottie().then(
        (lottie) =>
          new Promise((resolve, reject) => {
            let settled = false;

            const handleReady = () => {
              if (settled) return;
              settled = true;
              lottieIsReady = true;
              resetAnimation();
              resolve(lottieInstance);
            };

            const handleFailure = () => {
              if (settled) return;
              settled = true;
              lottieReadyPromise = null;
              lottieIsReady = false;
              if (lottieInstance && typeof lottieInstance.destroy === 'function') {
                lottieInstance.destroy();
              }
              lottieInstance = null;
              animationContainer.innerHTML = '';
              reject(new Error('Failed to load Lottie animation data.'));
            };

            lottieInstance = lottie.loadAnimation({
              container: animationContainer,
              renderer: 'svg',
              loop: true,
              autoplay: false,
              path: lottieSrc,
            });

            lottieInstance.addEventListener('DOMLoaded', handleReady);
            lottieInstance.addEventListener('data_failed', handleFailure);
          }),
      );

      return lottieReadyPromise;
    };

    const closeDialog = () => {
      if (canShowModal) {
        if (dialog.open) dialog.close();
      } else {
        dialog.removeAttribute('open');
        // The `close` event only fires for real dialogs, so tidy up here too.
        resetAnimation();
      }
    };

    const playAnimation = async () => {
      if (!lottieSrc || !animationContainer) return;

      try {
        const animation = await prepareAnimation();
        animation.goToAndPlay(0, true);
      } catch (error) {
        console.error('[wishlist-hint] Unable to load Lottie animation.', error);
      }
    };

    const openDialog = () => {
      if (canShowModal) {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      playAnimation();
    };

    /**
     * The wishlist grid is rendered asynchronously, so hold the dialog back
     * until the cards are on screen (or the wait times out).
     */
    const waitForWishlistGrid = () =>
      new Promise((resolve) => {
        const start = Date.now();

        const check = () => {
          const rendered = (scopeElement || document).querySelectorAll(
            '.wishlist-list-items .wishlist-list',
          ).length;

          if (rendered >= minimumItems) return resolve(true);
          if (Date.now() - start >= GRID_WAIT_TIMEOUT) return resolve(false);

          window.setTimeout(check, GRID_POLL_INTERVAL);
        };

        check();
      });

    const canShow = () =>
      !hasShown &&
      !isDismissed() &&
      isScopeVisible() &&
      getWishlistItems().length >= minimumItems;

    const wait = (ms) =>
      new Promise((resolve) => window.setTimeout(resolve, ms));

    const maybeShow = async () => {
      if (isPending || !canShow()) return;

      isPending = true;

      // Hold the dialog back for `showDelay`, measured from the moment the
      // customer landed on the list — time spent waiting for the grid counts
      // towards it rather than stacking on top.
      const startedAt = Date.now();
      await waitForWishlistGrid();
      const remainingDelay = showDelay - (Date.now() - startedAt);
      if (remainingDelay > 0) await wait(remainingDelay);

      isPending = false;

      if (!canShow()) return;

      hasShown = true;
      openDialog();
    };

    closeButtons.forEach((button) => {
      button.addEventListener('click', () => closeDialog());
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });

    dialog.addEventListener('close', () => {
      resetAnimation();
    });

    if (dismissCheckbox) {
      dismissCheckbox.checked = isDismissed();
      dismissCheckbox.addEventListener('change', () => {
        setDismissed(dismissCheckbox.checked);
      });
    }

    document.addEventListener('wishlist:change', () => {
      if (getWishlistItems().length < minimumItems) closeDialog();
    });

    // The account page toggles its tabs with an inline `display` style, so watch
    // the container and show the hint the moment the wishlist tab is revealed.
    if (scopeElement) {
      const scopeObserver = new MutationObserver(() => {
        if (hasShown) {
          scopeObserver.disconnect();
          return;
        }
        if (isScopeVisible()) maybeShow();
      });

      scopeObserver.observe(scopeElement, {
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden'],
      });
    }

    maybeShow();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlistCompareHint);
  } else {
    initWishlistCompareHint();
  }
})();
